import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { webSocketService } from '../services/websocket';
import { WebSocketMessage, ETIMFeature, User } from '../types/models'; // Добавлен импорт User
import { ApiService } from '../services/api';
import { log } from 'console';

const COOKIE_NAME = process.env.REACT_APP_AUTH_COOKIE_NAME || 'auth_token';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface AuthState {
  isAuthenticated: boolean;
  userId: number | null;
  user: User | null; // Добавлено поле user
  isLoading: boolean;
}

interface AppContextType extends AuthState {
  login: (nextUrl?: string) => void;
  logout: () => Promise<void>;
  isConnected: boolean;
  searchingSpnIds: Set<number>;
  addSearchingSpnId: (id: number) => void;
  removeSearchingSpnId: (id: number) => void;
  categoryFeatures: Record<number, ETIMFeature[]>;
  fetchCategoryFeatures: (categoryId: number) => Promise<void>;
  setSearchEventHandlers: (handlers: {
    onSearchComplete?: (spnId: number) => void;
    onSearchError?: (spnId: number, error?: string) => void;
    onBrandReselectionComplete?: (applicationId: number, processed?: number, skipped?: number) => void;
    onBrandReselectionError?: (applicationId: number, error?: string) => void;
    onFileProcessingError?: (filename: string, error: string) => void;
  } | null) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    userId: null,
    user: null, // Инициализируем user как null
    isLoading: true,
  });
  const navigate = useNavigate();

  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [searchingSpnIds, setSearchingSpnIds] = useState<Set<number>>(new Set());
  const [categoryFeatures, setCategoryFeatures] = useState<Record<number, ETIMFeature[]>>({});
  const [searchEventHandlers, setSearchEventHandlersState] = useState<{
    onSearchComplete?: (spnId: number) => void;
    onSearchError?: (spnId: number, error?: string) => void;
    onBrandReselectionComplete?: (applicationId: number, processed?: number, skipped?: number) => void;
    onBrandReselectionError?: (applicationId: number, error?: string) => void;
    onFileProcessingError?: (filename: string, error: string) => void;
  } | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      console.log("AppContext: Checking authentication status...");
      try {
        const { user } = await ApiService.checkAuthStatus(); // Получаем полный объект user
        if (user !== null) {
          console.log("AppContext: User authenticated with ID:", user.id);
          setAuthState({ isAuthenticated: true, userId: user.id, user: user, isLoading: false }); // Сохраняем user
          
          // Подключаемся к WebSocket после успешной аутентификации
          console.log("AppContext: Connecting to WebSocket...");
          try {
            await webSocketService.connect(user.id);
            console.log("AppContext: WebSocket connected successfully");
          } catch (wsError) {
            console.error("AppContext: Failed to connect WebSocket:", wsError);
          }
        } else {
          console.log("AppContext: User not authenticated.");
          setAuthState({ isAuthenticated: false, userId: null, user: null, isLoading: false }); // Обнуляем user
        }
      } catch (error) {
        console.error("AppContext: Error during authentication check:", error);
        setAuthState({ isAuthenticated: false, userId: null, user: null, isLoading: false }); // Обнуляем user
      }
    };

    checkAuth();
  }, []); // Пустой массив зависимостей означает, что эффект выполняется один раз при монтировании компонента

  const login = useCallback((nextUrl: string = '/') => {
    navigate(`/login?next=${encodeURIComponent(nextUrl)}`);
  }, [navigate]);

  const logout = useCallback(async () => {
    try {
      // Отключаемся от WebSocket перед выходом
      if (webSocketService.isConnected()) {
        console.log("AppContext: Disconnecting WebSocket before logout.");
        webSocketService.disconnect();
      }
      await fetch(`${API_BASE_URL}/logout`, { method: 'GET' });
    } catch (error) {
      console.error("Logout failed on backend:", error);
    } finally {
      Cookies.remove(COOKIE_NAME);
      setAuthState({ isAuthenticated: false, userId: null, user: null, isLoading: false }); // Обнуляем user при выходе
      navigate('/');
    }
  }, [navigate]);

  const addSearchingSpnId = useCallback((id: number) => {
    setSearchingSpnIds(prev => new Set(prev).add(id));
  }, []);

  const removeSearchingSpnId = useCallback((id: number) => { // test
    console.log(`AppContext: removeSearchingSpnId called for spn_id: ${id}`);
    setSearchingSpnIds(prev => {
      const newSet = new Set(prev);
      const deleted = newSet.delete(id);
      if (deleted) {
        console.log(`AppContext: spn_id: ${id} removed from searchingSpnIds. New set size: ${newSet.size}`);
      } else {
        console.warn(`AppContext: spn_id: ${id} was not found in searchingSpnIds for removal.`);
      }
      return newSet;
    });
  }, []);

  const handleWebSocketMessage = React.useCallback((message: WebSocketMessage) => {
    console.log('AppContext: Received WebSocket message:', message);
    if (message.event === 'search_complete' && message.spn_id) {
      console.log(`AppContext: Received 'search_complete' event for spn_id: ${message.spn_id}`);
      removeSearchingSpnId(message.spn_id);
      if (searchEventHandlers?.onSearchComplete) {
        console.log(`AppContext: Calling onSearchComplete handler for spn_id: ${message.spn_id}`);
        searchEventHandlers.onSearchComplete(message.spn_id);
      } else {
        console.log(`AppContext: onSearchComplete handler not set for spn_id: ${message.spn_id}`);
      }
    } else if (message.event === 'search_error' && message.spn_id) {
      console.log(`AppContext: Received 'search_error' event for spn_id: ${message.spn_id}, error: ${message.error}`);
      removeSearchingSpnId(message.spn_id);
      if (searchEventHandlers?.onSearchError) {
        console.log(`AppContext: Calling onSearchError handler for spn_id: ${message.spn_id}`);
        searchEventHandlers.onSearchError(message.spn_id, message.error);
      } else {
        console.log(`AppContext: onSearchError handler not set for spn_id: ${message.spn_id}`);
      }
    } else if (message.event === 'brand_reselection_complete' && message.application_id) {
      console.log(`AppContext: Received 'brand_reselection_complete' event for application_id: ${message.application_id}`);
      if (searchEventHandlers?.onBrandReselectionComplete) {
        console.log(`AppContext: Calling onBrandReselectionComplete handler for application_id: ${message.application_id}`);
        searchEventHandlers.onBrandReselectionComplete(message.application_id, message.processed_spns, message.skipped_spns);
      } else {
        console.log(`AppContext: onBrandReselectionComplete handler not set for application_id: ${message.application_id}`);
      }
    } else if (message.event === 'brand_reselection_error' && message.application_id) {
      console.log(`AppContext: Received 'brand_reselection_error' event for application_id: ${message.application_id}, error: ${message.error}`);
      if (searchEventHandlers?.onBrandReselectionError) {
        console.log(`AppContext: Calling onBrandReselectionError handler for application_id: ${message.application_id}`);
        searchEventHandlers.onBrandReselectionError(message.application_id, message.error);
      } else {
        console.log(`AppContext: onBrandReselectionError handler not set for application_id: ${message.application_id}`);
      }
    } else if (message.event === 'file_processing_error' && message.filename) {
      console.log(`AppContext: Received 'file_processing_error' event for filename: ${message.filename}, error: ${message.error}`);
      if (searchEventHandlers?.onFileProcessingError) {
        console.log(`AppContext: Calling onFileProcessingError handler for filename: ${message.filename}`);
        searchEventHandlers.onFileProcessingError(message.filename, message.error || 'Unknown error');
      } else {
        console.log(`AppContext: onFileProcessingError handler not set for filename: ${message.filename}`);
      }
    }
  }, [searchEventHandlers, removeSearchingSpnId]);

  useEffect(() => {
    // Подписываемся на статус подключения WebSocket для обновления UI
    const unsubscribe = webSocketService.subscribeToConnectionStatus((newIsConnectedStatus) => {
        console.log(`AppContext: Connection status updated by WebSocketService: ${newIsConnectedStatus}`);
        setIsConnected(newIsConnectedStatus);
    });

    return () => {
      unsubscribe();
      console.log("AppContext: Unsubscribing from connection status.");
    };
  }, []);

  useEffect(() => {
    webSocketService.addMessageHandler(handleWebSocketMessage);
    console.log('AppContext: WebSocket message handler added.');
    return () => {
      webSocketService.removeMessageHandler(handleWebSocketMessage);
    };
  }, [handleWebSocketMessage]);

  const setSearchEventHandlers = useCallback((handlers: {
    onSearchComplete?: (spnId: number) => void;
    onSearchError?: (spnId: number, error?: string) => void;
    onBrandReselectionComplete?: (applicationId: number, processed?: number, skipped?: number) => void;
    onBrandReselectionError?: (applicationId: number, error?: string) => void;
  } | null) => {
    setSearchEventHandlersState(handlers);
  }, []);

  const fetchCategoryFeatures = useCallback(async (categoryId: number) => {
    if (categoryFeatures[categoryId]) {
      console.log(`Category features for category ${categoryId} already loaded.`);
      return;
    }
    console.log(`Fetching category features for category ${categoryId}...`);
    try {
      const features = await ApiService.getCategoryFeatures(categoryId);
      setCategoryFeatures(prev => ({
        ...prev,
        [categoryId]: features,
      }));
      console.log(`Category features for category ${categoryId} loaded:`, features.length);
    } catch (error) {
      console.error(`Failed to fetch category features for category ${categoryId}:`, error);
      setCategoryFeatures(prev => ({
        ...prev,
        [categoryId]: [],
      }));
    }
  }, [categoryFeatures]);

  const contextValue: AppContextType = {
    ...authState,
    login,
    logout,
    isConnected,
    searchingSpnIds,
    addSearchingSpnId,
    removeSearchingSpnId,
    categoryFeatures,
    fetchCategoryFeatures,
    setSearchEventHandlers,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};